"use client";

import { Check, Search, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChatAvatar } from "@/components/chat/chat-avatar";
import {
  EventCompositeAvatar,
  SessionCompositeAvatar,
} from "@/components/chat/chat-composite-avatar";
import type { ChatListItem } from "@/hooks/use-chat";
import { chatHaptics } from "@/lib/chat-haptics";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import { cn } from "@/lib/utils";
import { GLASS_FIELD, GLASS_MUTED_TEXT } from "./liquid-glass-sheet";

/**
 * The "pick chats to send to" list: search, a row of recent chats, then every
 * conversation. Shared by the Send to sheet (SendToChatDrawer) and the Share
 * to Hiive screen, so both pick targets the same way.
 */

export const MAX_SHARE_TARGETS = 5;

/** Selected chat ids, capped at MAX_SHARE_TARGETS with a toast past it. */
export function useChatTargetSelection() {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const toggleChat = (chatId: number) => {
    chatHaptics.attachmentTrayOpen();
    setSelectedIds((previous) => {
      if (previous.includes(chatId)) {
        return previous.filter((id) => id !== chatId);
      }
      if (previous.length >= MAX_SHARE_TARGETS) {
        toast.error(`You can send to up to ${MAX_SHARE_TARGETS} chats at once`);
        return previous;
      }
      return [...previous, chatId];
    });
  };
  return {
    selectedIds,
    toggleChat,
    /** Select without the tap haptic (a preselection, not a tap). */
    selectChat: useCallback(
      (chatId: number) =>
        setSelectedIds((previous) =>
          previous.includes(chatId) || previous.length >= MAX_SHARE_TARGETS
            ? previous
            : [...previous, chatId],
        ),
      [],
    ),
    clearSelection: () => setSelectedIds([]),
  };
}

export function SendToChatAvatar({
  chat,
  size,
}: {
  chat: ChatListItem;
  size: "sm" | "lg";
}) {
  if (chat.type === "activity_session" && chat.activitySessionData) {
    const ActivityIcon = getIconComponent(
      chat.activitySessionData.activityIcon as IconType,
    );
    return (
      <SessionCompositeAvatar
        title={chat.name}
        activityIcon={ActivityIcon}
        creatorImageUrl={chat.activitySessionData.creatorImage ?? null}
        accentSeed={`${chat.activitySessionData.activityId ?? ""}:${chat.id}`}
        size={size}
      />
    );
  }

  if (chat.type === "event_occurrence" || chat.type === "event") {
    return (
      <EventCompositeAvatar
        title={chat.name}
        imageUrl={chat.eventOccurrenceData?.eventImageUrl ?? chat.image}
        hiiveLogoUrl={chat.eventOccurrenceData?.hiiveLogoUrl ?? null}
        size={size}
      />
    );
  }

  const directImage =
    chat.type === "direct" ? chat.directChatParticipant?.userImage : null;
  const directFallback =
    chat.type === "direct"
      ? chat.directChatParticipant?.userName ||
        chat.directChatParticipant?.username ||
        chat.name
      : chat.name;

  return (
    <ChatAvatar
      imageUrl={directImage ?? chat.image}
      fallback={directFallback || "C"}
      size={size}
    />
  );
}

/**
 * Search field plus the scrolling chat list. Renders two siblings meant for a
 * flex column: the field (shrink-0) and the list (flex-1, scrolls).
 */
export function ChatTargetPicker({
  chats,
  isLoading,
  query,
  onQueryChange,
  selectedIds,
  onToggle,
}: {
  chats: ChatListItem[];
  isLoading: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  selectedIds: number[];
  onToggle: (chatId: number) => void;
}) {
  const visibleChats = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const candidates = chats.filter((chat) => !chat.isGroupLeft);
    if (!normalized) return candidates;
    return candidates.filter((chat) =>
      (chat.name || "").toLowerCase().includes(normalized),
    );
  }, [chats, query]);

  const recentChats = useMemo(() => visibleChats.slice(0, 8), [visibleChats]);

  return (
    <>
      <div className="shrink-0 px-4 pb-2">
        <label
          className={cn(
            "flex h-11 items-center gap-2 rounded-full pr-1.5 pl-3.5",
            GLASS_FIELD,
          )}
        >
          <Search className={cn("size-4 shrink-0", GLASS_MUTED_TEXT)} />
          {/* 16px below md: anything smaller makes iOS zoom the page on
              focus, and the viewport never zooms back out. */}
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search"
            aria-label="Search chats"
            autoComplete="off"
            enterKeyHint="search"
            className="h-full min-w-0 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-tertiary focus:outline-none md:text-sm dark:text-white dark:placeholder:text-white/40"
          />
          {query ? (
            <button
              type="button"
              onClick={() => onQueryChange("")}
              aria-label="Clear search"
              className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-tertiary active:bg-black/5 dark:text-white/55 dark:active:bg-white/10"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-1">
        {recentChats.length > 0 && !query ? (
          <div className="flex [scrollbar-width:none] gap-1 overflow-x-auto px-1 pt-1 pb-3">
            {recentChats.map((chat) => {
              const isSelected = selectedIds.includes(chat.id);
              return (
                <button
                  key={`recent-${chat.id}`}
                  type="button"
                  onClick={() => onToggle(chat.id)}
                  aria-pressed={isSelected}
                  className="flex w-[4.5rem] shrink-0 flex-col items-center gap-1.5 rounded-2xl py-1 transition-transform duration-150 ease-out-strong active:scale-95"
                >
                  <span
                    className={cn(
                      "relative rounded-[14px] ring-offset-2 ring-offset-transparent transition-shadow duration-150 ease-out-strong",
                      isSelected && "ring-2 ring-brand",
                    )}
                  >
                    <SendToChatAvatar chat={chat} size="lg" />
                    {isSelected ? (
                      <span className="absolute -right-0.5 -bottom-0.5 flex size-5 items-center justify-center rounded-full border-2 border-white bg-brand text-white dark:border-[#1c1c1f]">
                        <Check className="size-3" strokeWidth={3} />
                      </span>
                    ) : null}
                  </span>
                  <span className="w-full truncate px-0.5 text-center text-[11px] leading-tight text-text-secondary dark:text-white/75">
                    {chat.name}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        <div
          className={cn(
            "space-y-0.5",
            // With nothing selected there is no send footer, so the list
            // itself has to clear the home indicator.
            selectedIds.length > 0
              ? "pb-3"
              : "pb-[max(env(safe-area-inset-bottom),0.75rem)]",
          )}
        >
          {isLoading ? (
            <ChatTargetListSkeleton />
          ) : visibleChats.length === 0 ? (
            <p
              className={cn("px-4 py-8 text-center text-sm", GLASS_MUTED_TEXT)}
            >
              {query ? "No chats match that." : "No chats yet."}
            </p>
          ) : (
            visibleChats.map((chat) => {
              const isSelected = selectedIds.includes(chat.id);
              return (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => onToggle(chat.id)}
                  aria-pressed={isSelected}
                  className={cn(
                    "flex min-h-14 w-full items-center justify-between gap-3 rounded-2xl px-2.5 py-2 text-left transition-colors duration-150 ease-out-strong",
                    isSelected
                      ? "bg-brand/10 dark:bg-brand/20"
                      : "active:bg-black/[0.05] dark:active:bg-white/[0.07] pointer-fine:hover:bg-black/[0.03] dark:pointer-fine:hover:bg-white/[0.04]",
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <SendToChatAvatar chat={chat} size="sm" />
                    <div className="min-w-0">
                      <div className="truncate text-[15px] font-medium text-text-primary dark:text-white">
                        {chat.name}
                      </div>
                      {chat.subtitle ? (
                        <div
                          className={cn(
                            "truncate text-[13px]",
                            GLASS_MUTED_TEXT,
                          )}
                        >
                          {chat.subtitle}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "flex size-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-150 ease-out-strong",
                      isSelected
                        ? "border-brand bg-brand text-white"
                        : "border-black/20 dark:border-white/25",
                    )}
                  >
                    {isSelected ? (
                      <Check className="size-3" strokeWidth={3} />
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

/** Same row geometry as a loaded chat, so nothing shifts when they land. */
function ChatTargetListSkeleton() {
  return (
    <div aria-hidden="true" className="space-y-0.5">
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="flex min-h-14 items-center gap-3 px-2.5 py-2"
        >
          <span className="size-8 shrink-0 animate-pulse rounded-full bg-black/[0.06] dark:bg-white/[0.08]" />
          <span className="min-w-0 flex-1 space-y-1.5">
            <span className="block h-3.5 w-2/5 animate-pulse rounded-full bg-black/[0.06] dark:bg-white/[0.08]" />
            <span className="block h-3 w-1/4 animate-pulse rounded-full bg-black/[0.05] dark:bg-white/[0.06]" />
          </span>
        </div>
      ))}
    </div>
  );
}
